import { Bad } from '@core/model/framework/Result/BadResult';
import {
  WaitOnCaseStateNotificationGateway,
  WaitOnCaseStateNotificationGatewayInput,
  WaitOnCaseStateNotificationGatewayOutput,
} from '@core/model/gateway/waitOnCaseStateNotificationGateway';
import { GetCaseState, GetCaseStateInput, GetCaseStateOutput } from '@core/usecase/getCaseState';
import { SERVICE_ID } from '@di/serviceId';
import { inject, injectable } from 'inversify';
import { BehaviorSubject, TimeoutError } from 'rxjs';
import { Observable } from 'rxjs/internal/Observable';
import { firstValueFrom } from 'rxjs/internal/firstValueFrom';
import { from } from 'rxjs/internal/observable/from';
import { poll } from '../../../model/framework/notifications/polling';

@injectable()
export class WaitOnCaseStateNotificationViaPolling implements WaitOnCaseStateNotificationGateway {
  private manualForceStopPolling$: BehaviorSubject<boolean> = new BehaviorSubject(false);

  constructor(
    @inject(GetCaseState)
    private readonly getCaseState: GetCaseState,
    @inject(SERVICE_ID.waitOnCaseStateTimeoutInSeconds)
    private readonly timeoutInSeconds: number,
    @inject(SERVICE_ID.waitOnCaseStateIntervalInSeconds)
    private readonly intervalInSeconds: number
  ) {}

  stopWaitingCaseStateUpdate(): void {
    this.manualForceStopPolling$.next(true);
    console.log('Polling - stopWaitingCaseStateUpdate');
  }

  async awaitCaseStateUpdate(
    input: WaitOnCaseStateNotificationGatewayInput
  ): WaitOnCaseStateNotificationGatewayOutput {
    try {
      const caseState = await firstValueFrom(
        poll<WaitOnCaseStateNotificationGatewayInput, Awaited<GetCaseStateOutput>>({
          intervalInSeconds: this.intervalInSeconds,
          timeoutInSeconds: this.timeoutInSeconds,
          apiCall: () =>
            this.getCaseStateApiCall({
              userId: input.userId,
              userToken: input.userToken,
            }),
          shouldContinuePolling: (caseState) => this.shouldContinuePolling(caseState),
          manualForceStop: this.manualForceStopPolling$,
        })
      );

      if (caseState.success === false) {
        throw new Error(`awaitCaseStateUpdate via polling, reason : ${caseState.reason}`);
      }

      return caseState;
    } catch (e) {
      if (e instanceof TimeoutError) {
        return Bad('CASE_STILL_IN_PROGRESS');
      } else if (e === 'STOP_POLLING_VIA_USER_ACTION') {
        this.resetUsecaseState();
        return Bad('STOP_POLLING_VIA_USER_ACTION');
      }
      throw e;
    }
  }

  private getCaseStateApiCall = (input: GetCaseStateInput): Observable<Awaited<GetCaseStateOutput>> => {
    return new Observable((observer) => {
      from(
        this.getCaseState.execute({
          userId: input.userId,
          userToken: input.userToken,
        })
      ).subscribe({
        next: (result) => {
          observer.next(result);
        },
      });
    });
  };

  private shouldContinuePolling = (caseState: Awaited<GetCaseStateOutput>): boolean => {
    if (caseState.success === true && caseState.value === 'IN_PROGRESS') {
      return true;
    }

    return false;
  };

  private resetUsecaseState() {
    this.manualForceStopPolling$.next(false);
  }
}
